import { PolygonLayer, GeoJsonLayer, IconLayer, ScatterplotLayer } from '@deck.gl/layers';
import { latLngToCell, cellToBoundary } from 'h3-js';
import { hexColor } from './trafficDataset';
import { losColor, kmhToMphExact } from './networkEditor';

// Sampling step per H3 resolution, in degrees: roughly half a cell width so no cell is skipped.
const STEP_BY_RESOLUTION = { 6: 0.018, 7: 0.007, 8: 0.0026, 9: 0.001 };

// Above this the grid is not worth drawing, and is unusable to click anyway.
export const MAX_HEX_CELLS = 4000;

/** Build the H3 grid covering `bbox`. Returns { cells, suppressed }: over MAX_HEX_CELLS
 *  the grid is suppressed rather than drawn partially. Already-selected cells are always
 *  included so a selection stays visible after panning away. */
export function generateHexagons(bbox, resolution, selectedHexagons) {
  const step = STEP_BY_RESOLUTION[resolution] || 0.0026;
  const cols = Math.ceil((bbox[2] - bbox[0]) / step);
  const rows = Math.ceil((bbox[3] - bbox[1]) / step);

  const toCell = (hexId) => ({
    hexId,
    coordinates: cellToBoundary(hexId).map(([lat, lng]) => [lng, lat]),
    isSelected: selectedHexagons.has(hexId),
  });
  const selectedOnly = () => ({
    cells: Array.from(selectedHexagons).map(toCell),
    suppressed: true,
  });

  // Bail before the loop when the area is hopeless — the guard after the loop
  // would be too late, since the loop itself is the expensive part.
  if (cols <= 0 || rows <= 0 || cols * rows > MAX_HEX_CELLS * 12) return selectedOnly();

  const hexagons = new Set();
  for (let lat = bbox[1]; lat <= bbox[3]; lat += step) {
    for (let lng = bbox[0]; lng <= bbox[2]; lng += step) {
      hexagons.add(latLngToCell(lat, lng, resolution));
    }
  }
  if (hexagons.size > MAX_HEX_CELLS) return selectedOnly();

  for (const id of selectedHexagons) hexagons.add(id);
  return { cells: Array.from(hexagons).map(toCell), suppressed: false };
}

export function getHexLayer(hexData, selectedHexagons, setSelectedHexagons, showHexagons, hexIdToCoordinates) {
    if (!showHexagons) return null;
  return new PolygonLayer({
    id: 'hexagon-layer',
    data: hexData,
    pickable: true,
    stroked: true,
    filled: true,
    wireframe: true,
    lineWidthMinPixels: 2,
    getPolygon: d => d.coordinates,
    getFillColor: d => d.isSelected ? [124, 198, 68, 180] : [255, 255, 255, 30],
    getLineColor: [124, 198, 68, 180],
    onClick: (info) => {
      if (info.object) {
        const hexId = info.object.hexId;
        const hexCoords = info.object.coordinates;
        hexCoords.push(hexCoords[0]); // Close the polygon
        const newSelected = new Set(selectedHexagons);
        if (newSelected.has(hexId)) {
          newSelected.delete(hexId);
          hexIdToCoordinates.current.delete(hexId);
        } else {
          newSelected.add(hexId);
          hexIdToCoordinates.current.set(hexId, [hexCoords]);
        }
        setSelectedHexagons(newSelected);
      }
    }
  });
}

export function getPolyLayer(selectedPolygons) {
    const geoJson = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: selectedPolygons,
          },
        },
      ],
    };

  return new GeoJsonLayer({
    id: 'search-result-polygon',
    data: geoJson,
    stroked: true,
    filled: true,
    lineWidthMinPixels: 2,
    getLineColor: [106, 179, 58],      // #6ab33a
    getFillColor: [124, 198, 68, 80],  // #7CC644 with opacity
    pickable: false,
  });
}

export function extractPolygonFromFeature(feature, setSelectedPolygons, setSelectedMapboxId, setDimension) {
    var polygon;
    if (feature.properties.bbox) {
      const [[minLng, minLat, maxLng, maxLat]] = [feature.properties.bbox];
      polygon = [
        [minLng, minLat],
        [maxLng, minLat],
        [maxLng, maxLat],
        [minLng, maxLat],
        [minLng, minLat]
      ];
    } else {
      const lng = feature.properties.coordinates.longitude;
      const lat = feature.properties.coordinates.latitude;
      const radius = 0.005;
      polygon = [
        [lng - radius, lat - radius],
        [lng + radius, lat - radius],
        [lng + radius, lat + radius],
        [lng - radius, lat + radius],
        [lng - radius, lat - radius]
      ];
    }
    const mapbox_id = feature.properties.mapbox_id;
    const results = { [mapbox_id]: [polygon] };
    setSelectedPolygons(results);
    setSelectedMapboxId(mapbox_id);
    setDimension("link");
}

export function getTrafficLayer(dimension, trafficData, hexIdToCoordinates) {
  const TrafficColors = (volume) => {
    if (volume > 800) return [255, 0, 0, 180];      // Red (High Traffic)
    if (volume > 600) return [255, 165, 0, 180];    // Orange
    if (volume > 400) return [255, 255, 0, 180];    // Yellow
    if (volume > 200) return [0, 255, 0, 180];      // Green
    return [0, 128, 255, 180];                      // Blue (Low Traffic)
  };

  if (!trafficData || Object.keys(trafficData).length === 0) return null;
  if (!dimension) return null;

  if (dimension === "hexagon") {
    hexIdToCoordinates = Object.fromEntries(hexIdToCoordinates);
    const hexagonData = Object.keys(trafficData).map(hexId => {
      const cell = trafficData[hexId];
      const coordinates = hexIdToCoordinates[hexId];
      return {
        hexId,
        coordinates: coordinates ? coordinates[0] : [],
        // Normalised shape from utils/trafficDataset: { value, metric,
        // edgeCount, detail }. `value` is null when the cell holds no road —
        // which must not render as "free-flowing".
        cell,
      };
    }).filter(item => item.coordinates.length > 0);
    return new PolygonLayer({
      id: 'traffic-hexagon-layer',
      data: hexagonData,
      pickable: true,
      stroked: true,
      filled: true,
      wireframe: false,
      lineWidthMinPixels: 1,
      getPolygon: d => d.coordinates,
      getFillColor: d => hexColor(d.cell),
      getLineColor: [255, 255, 255, 100],
      getLineWidth: 1,
      updateTriggers: { getFillColor: [trafficData] },
      onHover: ({ object, x, y }) => {
        const tooltip = document.getElementById('tooltip');
        if (object) {
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          const rows = (object.cell?.detail || [])
            .map(([k, v]) => `<b>${k}:</b> ${v}`)
            .join('<br/>');
          tooltip.innerHTML = rows || '<i>No road in this cell</i>';
        } else {
          tooltip.style.display = 'none';
        }
      },
    });
  } else if (dimension === "link") {
    // Two feature shapes reach this layer: the mock tables' (Volume/Speed/
    // VehicleCount) and the serving layer's (vc/los/vehCount/speedKmh). Branch
    // on `vc` rather than on a passed-in flag, so a feature always renders with
    // the scale that matches its own numbers.
    const isVc = (d) => d?.properties?.vc != null;
    return new GeoJsonLayer({
      id: "link-layer",
      data: trafficData,
      lineWidthScale: 2,
      lineWidthMinPixels: 5,
      getLineColor: (d) =>
        isVc(d) ? losColor(d.properties.vc, 220) : TrafficColors(d.properties?.Volume ?? 0),
      getLineWidth: (d) => d.properties?.Density || 1,
      pickable: true,
      updateTriggers: { getLineColor: [trafficData] },
      onHover: ({ object, x, y }) => {
        const tooltip = document.getElementById('tooltip');
        if (object) {
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          const p = object.properties || {};
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          tooltip.innerHTML = isVc(object)
            ? `<b>${p.name || p.edgeId}</b><br/>
               <b>V/C:</b> ${Number(p.vc).toFixed(3)} (LOS ${p.los})<br/>
               <b>Vehicles:</b> ${Number(p.vehCount || 0).toLocaleString()}<br/>
               <b>Speed:</b> ${kmhToMphExact(p.speedKmh).toFixed(1)} mph<br/>
               <b>Delay:</b> ${Number(p.delayS || 0).toFixed(1)} s`
            : `<b>LinkID:</b> ${p.LinkID}<br/>
               <b>Volume:</b> ${p.Volume}<br/>
               <b>Speed:</b> ${Number(p.Speed ?? 0).toFixed(2)}<br/>
               <b>Vehicle Count:</b> ${p.VehicleCount}`;
        } else {
          tooltip.style.display = 'none';
        }
      },
    })
  }
}

export function getRouteLayer(routeData) {
  if (!routeData || !routeData.features || routeData.features.length === 0) return null;

  // A route annotated with modelled conditions (from /api/traffic/route): draw
  // it by LOS rather than one flat colour, so the bottleneck on the path is
  // visible without opening anything. Segments the pipeline does not model are
  // drawn hollow-grey — never green, which would read as "clear".
  const isAnnotated = routeData.features.some(f => f.properties?.modelled !== undefined);
  if (isAnnotated) {
    return new GeoJsonLayer({
      id: 'route-layer',
      data: routeData,
      lineWidthScale: 1,
      lineWidthMinPixels: 6,
      pickable: true,
      updateTriggers: { getLineColor: [routeData] },
      getLineColor: (d) =>
        d.properties?.modelled ? losColor(d.properties.vc, 235) : [130, 130, 130, 150],
      getLineWidth: (d) => (d.properties?.modelled ? 7 : 4),
      onHover: ({ object, x, y }) => {
        const tooltip = document.getElementById('tooltip');
        if (object) {
          const p = object.properties || {};
          const canvas = document.querySelector('canvas');
          const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
          tooltip.style.display = 'block';
          tooltip.style.left = `${rect.left + x + 10}px`;
          tooltip.style.top = `${rect.top + y + 10}px`;
          tooltip.innerHTML = p.modelled
            ? `<b>${p.name || 'unnamed'}</b><br/>
               <b>V/C:</b> ${Number(p.vc).toFixed(3)} (LOS ${p.los})<br/>
               <b>Vehicles:</b> ${Number(p.vehCount || 0).toLocaleString()}<br/>
               <b>Speed:</b> ${kmhToMphExact(p.speedKmh).toFixed(1)} mph<br/>
               <b>Length:</b> ${Number(p.lengthKm || 0).toFixed(2)} km`
            : `<b>${p.name || 'unnamed'}</b><br/><i>Not in the modelled network</i>`;
        } else {
          tooltip.style.display = 'none';
        }
      },
    });
  }

  // Check if this is a multimodal route (has travel_mode property)
  const isMultimodal = routeData.features.some(f => f.properties?.travel_mode);

  // Parse hex color string to RGBA array
  const hexToRgba = (hex, alpha = 255) => {
    if (!hex) return [150, 150, 150, alpha];
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b, alpha];
  };

  return new GeoJsonLayer({
    id: 'route-layer',
    data: routeData,
    lineWidthScale: 1,
    lineWidthMinPixels: isMultimodal ? 2 : 5,
    getLineColor: (d) => {
      if (!isMultimodal) return [0, 128, 255, 200]; // Blue for non-multimodal
      const mode = d.properties?.travel_mode;
      if (mode === 'transit') {
        return hexToRgba(d.properties?.transit_color, 230);
      }
      return [150, 150, 150, 200]; // Gray for pedestrian
    },
    getLineWidth: (d) => {
      if (!isMultimodal) return 5;
      return d.properties?.travel_mode === 'transit' ? 6 : 3;
    },
    pickable: true,
    onHover: ({ object, x, y }) => {
      const tooltip = document.getElementById('tooltip');
      if (object && isMultimodal) {
        const canvas = document.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
        tooltip.style.display = 'block';
        tooltip.style.left = `${rect.left + x + 10}px`;
        tooltip.style.top = `${rect.top + y + 10}px`;
        const mode = object.properties?.travel_mode;
        if (mode === 'transit') {
          // Headsign, not just route name: an itinerary can board the same route
          // twice (out to a terminus, then back), and without the headsign the
          // two legs are indistinguishable.
          const headsign = object.properties?.transit_headsign;
          tooltip.innerHTML = `
            <b>${object.properties.transit_name}</b>${headsign ? ` &rarr; ${headsign}` : ''}
          `;
        } else {
          tooltip.innerHTML = `<b>Walk</b>`;
        }
      } else if (tooltip) {
        tooltip.style.display = 'none';
      }
    }
  });
}

// Category config with FA icon SVG paths (FA5 Free Solid) and colors
import { renderToStaticMarkup } from 'react-dom/server';
import { createElement } from 'react';
import {
  FaUtensils, FaCoffee, FaShoppingCart, FaHospital,
  FaCapsules, FaSchool, FaGasPump, FaParking,
  FaMoneyBillWave, FaHotel
} from 'react-icons/fa';
import { MdTraffic } from 'react-icons/md';

const CATEGORY_CONFIG = {
  restaurant:  { icon: FaUtensils,      color: '#FF6B6B', label: 'Restaurant' },
  cafe:        { icon: FaCoffee,        color: '#D4A574', label: 'Cafe' },
  supermarket: { icon: FaShoppingCart,  color: '#4ECDC4', label: 'Supermarket' },
  hospital:    { icon: FaHospital,      color: '#FF6B8A', label: 'Hospital' },
  pharmacy:    { icon: FaCapsules,      color: '#45B7D1', label: 'Pharmacy' },
  school:      { icon: FaSchool,        color: '#96CEB4', label: 'School' },
  gas_station: { icon: FaGasPump,       color: '#FFEAA7', label: 'Gas Station' },
  parking_lot: { icon: FaParking,       color: '#74B9FF', label: 'Parking' },
  atm:         { icon: FaMoneyBillWave, color: '#55E6C1', label: 'ATM' },
  hotel:       { icon: FaHotel,         color: '#A29BFE', label: 'Hotel' },
};

// Create SVG data URI for a category icon (colored circle + glyph).
// glyph/ring default to the POI look (white glyph, faint light ring); the
// signal markers below override both because they sit on a light disc.
function createIconDataUri(IconComponent, bgColor, glyphColor = 'white',
                           ringColor = 'rgba(255,255,255,0.4)') {
  const svgMarkup = renderToStaticMarkup(createElement(IconComponent, {}));
  const vbMatch = svgMarkup.match(/viewBox="([^"]+)"/);
  const viewBox = vbMatch ? vbMatch[1] : '0 0 512 512';
  const innerMatch = svgMarkup.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
  const innerContent = innerMatch ? innerMatch[1] : '';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="22" fill="${bgColor}" stroke="${ringColor}" stroke-width="1.5"/>
    <svg x="12" y="12" width="24" height="24" viewBox="${viewBox}" fill="${glyphColor}" stroke="none">
      ${innerContent}
    </svg>
  </svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// MdTraffic is Material Design (Apache 2.0) -- no attribution obligation.
// Deliberately not a red/amber/green signal head: red already means LOS F on the roads
// underneath.
let _signalIconUris = null;

export function getSignalIconUris() {
  if (_signalIconUris) return _signalIconUris;
  _signalIconUris = {
    idle:   createIconDataUri(MdTraffic, '#ffffff', '#1e1e1e', 'rgba(20,20,20,0.85)'),
    active: createIconDataUri(MdTraffic, '#ffc800', '#241a00', 'rgba(255,140,0,1)'),
  };
  return _signalIconUris;
}

// Cache generated icon data URIs
let _iconDataUris = null;

function getIconDataUris() {
  if (_iconDataUris) return _iconDataUris;
  _iconDataUris = {};
  for (const [cat, config] of Object.entries(CATEGORY_CONFIG)) {
    _iconDataUris[cat] = createIconDataUri(config.icon, config.color);
  }
  return _iconDataUris;
}

export { CATEGORY_CONFIG };

export function getPOILayer(facilityData, onClickFacility) {
  if (!facilityData || facilityData.length === 0) return null;

  const iconDataUris = getIconDataUris();

  return new IconLayer({
    id: 'poi-layer',
    data: facilityData,
    pickable: true,
    getIcon: d => {
      const cat = d.properties?._category || 'restaurant';
      return {
        url: iconDataUris[cat] || iconDataUris['restaurant'],
        width: 48,
        height: 48,
        anchorY: 24
      };
    },
    getPosition: d => d.geometry.coordinates,
    getSize: 36,
    sizeMinPixels: 24,
    sizeMaxPixels: 48,
    onClick: ({ object }) => {
      if (object && onClickFacility) {
        onClickFacility(object);
      }
    },
    onHover: ({ object, x, y }) => {
      const tooltip = document.getElementById('tooltip');
      if (object) {
        const canvas = document.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
        tooltip.style.display = 'block';
        tooltip.style.left = `${rect.left + x + 10}px`;
        tooltip.style.top = `${rect.top + y + 10}px`;
        const config = CATEGORY_CONFIG[object.properties?._category];
        tooltip.innerHTML = `
          <b>${object.properties?.name || 'Unknown'}</b><br/>
          ${config?.label || object.properties?._category || ''}
        `;
      } else {
        tooltip.style.display = 'none';
      }
    }
  });
}

// ── Transit layer helpers ────────────────────────────────────────────────────

const TRANSIT_CATEGORY_STYLE = {
  rail:  { widthMinPixels: 4, opacity: 220 },
  bus:   { widthMinPixels: 2, opacity: 180 },
  ferry: { widthMinPixels: 3, opacity: 180 },
  other: { widthMinPixels: 2, opacity: 160 },
};

// Parse '#RRGGBB' → [r, g, b] or null.
function parseHexColor(hex) {
  if (!hex || hex.length !== 7) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (isNaN(r) || isNaN(g) || isNaN(b)) ? null : [r, g, b];
}

// Default route colors when the feed provides none.
const TRANSIT_DEFAULT_RGB = {
  rail:  [30, 120, 200],
  bus:   [160, 160, 160],
  ferry: [20, 160, 160],
  other: [140, 100, 200],
};

export function getTransitRouteLayer(routesGeoJson) {
  if (!routesGeoJson || !routesGeoJson.features || routesGeoJson.features.length === 0) return null;
  return new GeoJsonLayer({
    id: 'transit-routes',
    data: routesGeoJson,
    pickable: true,
    lineWidthScale: 1,
    lineWidthMinPixels: 2,
    getLineColor: (d) => {
      const cat = (d.properties && d.properties.category) || 'bus';
      const style = TRANSIT_CATEGORY_STYLE[cat] || TRANSIT_CATEGORY_STYLE.other;
      const rgb = parseHexColor(d.properties && d.properties.color)
        || TRANSIT_DEFAULT_RGB[cat]
        || TRANSIT_DEFAULT_RGB.other;
      return [...rgb, style.opacity];
    },
    getLineWidth: (d) => {
      const cat = (d.properties && d.properties.category) || 'bus';
      return (TRANSIT_CATEGORY_STYLE[cat] || TRANSIT_CATEGORY_STYLE.other).widthMinPixels;
    },
    lineWidthUnits: 'pixels',
    updateTriggers: {
      getLineColor: [routesGeoJson],
      getLineWidth: [routesGeoJson],
    },
    onHover: ({ object, x, y }) => {
      const tooltip = document.getElementById('tooltip');
      if (!tooltip) return;
      if (object) {
        const canvas = document.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
        tooltip.style.display = 'block';
        tooltip.style.left = `${rect.left + x + 12}px`;
        tooltip.style.top = `${rect.top + y + 10}px`;
        const p = object.properties || {};
        const name = p.long_name || p.short_name || p.route_id || 'Route';
        const trips = p.trips_per_weekday > 0 ? `<br/>${p.trips_per_weekday} trips / weekday` : '';
        tooltip.innerHTML = `<b>${name}</b>${trips}`;
      } else {
        tooltip.style.display = 'none';
      }
    },
  });
}

// Low-income household share per TAZ hexagon (res 7), from /data/income/hex_income_res7.json.
function lerpColor(a, b, t) { return a.map((v, i) => Math.round(v + (b[i] - v) * t)); }
export function incomeColor(share, households) {
  if (share == null || households < 30) return [120, 120, 120, 35];
  const t = Math.max(0, Math.min(1, share / 0.8));
  const lo = [64, 86, 160, 50], mid = [140, 90, 200, 125], hi = [230, 70, 160, 205];
  return t < 0.5 ? lerpColor(lo, mid, t / 0.5) : lerpColor(mid, hi, (t - 0.5) / 0.5);
}

export function getIncomeHexLayer(cells, { pickable = true } = {}) {
  if (!cells || cells.length === 0) return null;
  return new PolygonLayer({
    id: 'income-hex-layer',
    data: cells,
    pickable,
    stroked: true,
    filled: true,
    lineWidthMinPixels: 0.5,
    getPolygon: d => d.polygon,
    getFillColor: d => incomeColor(d.share, d.hh),
    getLineColor: [255, 255, 255, 25],
    onHover: ({ object, x, y }) => {
      const tooltip = document.getElementById('tooltip');
      if (!tooltip) return;
      if (object) {
        const canvas = document.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
        tooltip.style.display = 'block';
        tooltip.style.left = `${rect.left + x + 12}px`;
        tooltip.style.top = `${rect.top + y + 10}px`;
        const share = object.share == null ? 'n/a' : `${Math.round(object.share * 100)}%`;
        const med = object.median == null ? 'n/a' : (object.median >= 250000 ? '$250k+' : `$${Math.round(object.median).toLocaleString()}`);
        tooltip.innerHTML = `<b>Zone ${object.taz}</b><br/>${object.hh.toLocaleString()} households<br/>${share} low-income (${object.low.toLocaleString()})<br/>Median income ${med}`;
      } else {
        tooltip.style.display = 'none';
      }
    },
  });
}

export function getTransitStopLayer(stopsGeoJson, showBusStops) {
  if (!stopsGeoJson || !stopsGeoJson.features || stopsGeoJson.features.length === 0) return null;
  // Rail stations and ferry terminals are always drawn; bus stops only when zoomed in
  // (MapContainer passes showBusStops from the zoom level).
  const features = showBusStops
    ? stopsGeoJson.features
    : stopsGeoJson.features.filter(f => f.properties && f.properties.category !== 'bus stop');
  if (features.length === 0) return null;
  return new ScatterplotLayer({
    id: 'transit-stops',
    data: features,
    pickable: true,
    opacity: 1,
    stroked: true,
    filled: true,
    radiusUnits: 'pixels',
    radiusMinPixels: 2,
    radiusMaxPixels: 8,
    lineWidthMinPixels: 1,
    getPosition: d => d.geometry.coordinates,
    getRadius: d => {
      const cat = d.properties && d.properties.category;
      return (cat === 'rail station' || cat === 'ferry terminal') ? 6 : 2.5;
    },
    getFillColor: d => {
      const cat = d.properties && d.properties.category;
      if (cat === 'rail station') return [30, 120, 200, 230];
      if (cat === 'ferry terminal') return [20, 160, 160, 230];
      return [190, 190, 190, 150];
    },
    getLineColor: d => {
      const cat = d.properties && d.properties.category;
      return (cat === 'rail station' || cat === 'ferry terminal') ? [255, 255, 255, 200] : [0, 0, 0, 0];
    },
    updateTriggers: { getRadius: [features], getFillColor: [features], getLineColor: [features] },
    onHover: ({ object, x, y }) => {
      const tooltip = document.getElementById('tooltip');
      if (!tooltip) return;
      if (object) {
        const canvas = document.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
        tooltip.style.display = 'block';
        tooltip.style.left = `${rect.left + x + 12}px`;
        tooltip.style.top = `${rect.top + y + 10}px`;
        const p = object.properties || {};
        let routes = '';
        if (p.routes_served && p.routes_served.length) {
          const names = p.routes_served.map(r => (r.includes(':') ? r.slice(r.indexOf(':') + 1) : r));
          routes = `<br/>${names.slice(0, 8).join(', ')}${names.length > 8 ? ` +${names.length - 8} more` : ''}`;
        }
        tooltip.innerHTML = `<b>${p.name || 'Stop'}</b><br/>${p.category || ''}${routes}`;
      } else {
        tooltip.style.display = 'none';
      }
    },
  });
}
